// Eagle Harbor Advisors YouTube video catalog. Powers the VideoObject JSON-LD
// emitted by the SEO component on matched routes, and the supplemental
// /sitemap-videos.xml referenced from robots.txt. The catalog is intentionally
// hand-curated (not pulled live from YouTube) so SEO output is deterministic
// at build time and the deployed bundle does not depend on YouTube being
// reachable. When new videos are published, add an entry here and (optionally)
// map it to a route via ROUTE_VIDEOS below.
//
// Channel: https://www.youtube.com/channel/UC7H7o70-Kjy0UhJD6kmwAXQ
// Source of metadata: channel RSS feed (titles, descriptions, upload dates).

import { SITE_ORIGIN, SITE_NAME } from "./Seo";

export type Video = {
  id: string; // YouTube video ID (e.g. "Kbzlc42P6L0")
  title: string;
  description: string;
  uploadDate: string; // ISO 8601
};

export const VIDEOS: Record<string, Video> = {
  Kbzlc42P6L0: {
    id: "Kbzlc42P6L0",
    title: "The MC Squared Difference",
    description:
      "An overview of Eagle Harbor's MC² wealth-management process — built on understanding the critical matters in your life, then applying control to deliver certainty across investments, tax strategy, estate planning, and insurance.",
    uploadDate: "2024-03-22T17:51:26+00:00",
  },
  "OjbBfyC6-KI": {
    id: "OjbBfyC6-KI",
    title: "Roth IRA: Convert or Contribute?",
    description:
      "Roth IRAs offer tax advantages traditional retirement accounts don't. Eagle Harbor walks through when a conversion makes sense versus a direct contribution.",
    uploadDate: "2023-11-13T16:01:29+00:00",
  },
  Yq2Wc60ZujM: {
    id: "Yq2Wc60ZujM",
    title: "5 Common Mistakes to Avoid in Retirement",
    description:
      "Five common retirement-planning mistakes — and the strategies Eagle Harbor uses to help clients avoid them and maximize long-term savings.",
    uploadDate: "2023-11-13T15:59:44+00:00",
  },
  a4wVIFvFNZc: {
    id: "a4wVIFvFNZc",
    title: "How to Plan Ahead in Caring for Aging Parents",
    description:
      "As parents age, families face challenges around health, finances, and living arrangements. Eagle Harbor explains how to plan ahead to reduce the stress on everyone involved.",
    uploadDate: "2023-11-12T02:06:42+00:00",
  },
  "3r3fXsUqw58": {
    id: "3r3fXsUqw58",
    title: "Why is Asset Allocation Important to Investing?",
    description:
      "Asset allocation is the foundation of a resilient portfolio — balancing risk, diversifying holdings, and aligning the mix to each investor's goals and time horizon.",
    uploadDate: "2023-11-12T01:55:38+00:00",
  },
  "5eV9Jx0YSAg": {
    id: "5eV9Jx0YSAg",
    title: "How to be Tax-Efficient with Your Investments",
    description:
      "Key tax-efficiency strategies for investors — including asset location, tax-loss harvesting, and account sequencing — explained by the Eagle Harbor team.",
    uploadDate: "2023-11-12T01:53:49+00:00",
  },
  zQ6seUEWhsg: {
    id: "zQ6seUEWhsg",
    title: "The Silent Killer: The Impact of Inflation on Retirement",
    description:
      "Inflation quietly erodes purchasing power over a multi-decade retirement. Eagle Harbor explains the minimum return your portfolio needs to keep up.",
    uploadDate: "2023-10-31T21:02:59+00:00",
  },
  jFr6ebofhmg: {
    id: "jFr6ebofhmg",
    title: "Is Tax Planning Missing in your Retirement Planning?",
    description:
      "Most retirement plans focus on accumulation but skip the tax side. Eagle Harbor walks through why tax planning belongs at the center of every retirement strategy.",
    uploadDate: "2023-10-31T21:00:59+00:00",
  },
  uhtiiorEKfw: {
    id: "uhtiiorEKfw",
    title: "Don't Let Timing Ruin Your Retirement",
    description:
      "When you begin drawing income from your investments can have an outsized impact on long-term wealth. Eagle Harbor explains sequence-of-returns risk and how to manage it.",
    uploadDate: "2023-10-28T00:51:30+00:00",
  },
  fI9JtdYOwBQ: {
    id: "fI9JtdYOwBQ",
    title: "Is Estate Planning Just For The Rich?",
    description:
      "Wills, living wills, and powers of attorney are essential for every family — not just the wealthy. Eagle Harbor breaks down what every household needs in place.",
    uploadDate: "2023-10-28T00:49:51+00:00",
  },
  HqH7sna8jMM: {
    id: "HqH7sna8jMM",
    title: "How to Maximize Social Security Survivor Benefits",
    description:
      "Survivor benefits are often overlooked when couples file for Social Security. Eagle Harbor explains how filing choices can affect a surviving spouse's lifetime income.",
    uploadDate: "2023-10-26T01:05:42+00:00",
  },
  CO_Ielbtmcw: {
    id: "CO_Ielbtmcw",
    title: "How To Strategize for Your Social Security Benefits",
    description:
      "Social Security filing strategy can move hundreds of thousands of dollars in lifetime benefits. Eagle Harbor walks through how to think about timing and coordination.",
    uploadDate: "2023-10-26T00:53:49+00:00",
  },
  "-Ex4uBBrp5U": {
    id: "-Ex4uBBrp5U",
    title: "How Are Your Social Security Benefits Calculated?",
    description:
      "How Social Security benefits are actually computed — and why your filing strategy should be informed by income needs, other sources, taxes, and health.",
    uploadDate: "2023-10-26T00:49:40+00:00",
  },
  hKX1Cum3TDE: {
    id: "hKX1Cum3TDE",
    title: "What's Your Inflation-Fighting Strategy?",
    description:
      "Inflation diminishes the buying power of every dollar in your portfolio. Eagle Harbor explains how to position investments to defend purchasing power over time.",
    uploadDate: "2023-10-18T01:13:00+00:00",
  },
  IGRJc2_ASrE: {
    id: "IGRJc2_ASrE",
    title: "The Effects of Inflation on Retirement",
    description:
      "Inflation's long-tail impact on retirement savings — and the questions Eagle Harbor asks to make sure each client's plan stays on track in higher-cost environments.",
    uploadDate: "2023-10-18T01:11:14+00:00",
  },
};

// Route → videoIds. Drives VideoObject JSON-LD emission in the SEO component.
// Keep selections topically tight (1–3 per page); over-stuffing dilutes signal
// and risks being treated as boilerplate by Google.
export const ROUTE_VIDEOS: Record<string, string[]> = {
  "/emc2": ["Kbzlc42P6L0"],
  "/services": [
    "Yq2Wc60ZujM", // 5 Common Mistakes to Avoid in Retirement
    "5eV9Jx0YSAg", // How to be Tax-Efficient with Your Investments
    "jFr6ebofhmg", // Is Tax Planning Missing in your Retirement Planning?
  ],
  "/portfolio": [
    "3r3fXsUqw58", // Why is Asset Allocation Important to Investing?
    "hKX1Cum3TDE", // What's Your Inflation-Fighting Strategy?
  ],
};

export const youtubeWatchUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;
export const youtubeEmbedUrl = (id: string) => `https://www.youtube.com/embed/${id}`;
export const youtubeThumbnailUrl = (id: string) =>
  `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

// Build a schema.org VideoObject JSON-LD document for a single video. Returns
// undefined if the videoId is not in the catalog (defensive — should not happen
// at build time, but keeps the page render resilient).
export const buildVideoObjectJsonLd = (
  videoId: string
): Record<string, any> | undefined => {
  const v = VIDEOS[videoId];
  if (!v) return undefined;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.title,
    description: v.description,
    thumbnailUrl: youtubeThumbnailUrl(v.id),
    uploadDate: v.uploadDate,
    contentUrl: youtubeWatchUrl(v.id),
    embedUrl: youtubeEmbedUrl(v.id),
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/eha_logo.png`,
      },
    },
  };
};
